import { push } from "redux-first-history";

import API from "api";
import { NetworkRequest } from "api/actions";

import { isSaveBlockedByMonthEndMaintenance } from "modules/maintenance/guard";
import { ApplicationState as AppState, AppThunkAction } from "store";
import * as actions from "modules/benefits/spendingAccounts/actions";
import {
    MembershipCoveragePeriodSummary,
    MembershipSpendingAccountList
} from "modules/benefits/spendingAccounts/models";

import { AddNotificationAction } from "modules/notifications/actions";
import { NotificationStyle } from "modules/notifications/models";
import { UpdatePersonMaskedSocialInsuranceNumber } from "modules/person/actions";
import { Formatter } from "modules/utils";
import { RouterAction } from "routes";

export type HandledAction =
    | actions.GetCoveragePeriodsInitAction
    | actions.GetCoveragePeriodsCompleteAction
    | actions.GetSpendingAccountsInitAction
    | actions.GetSpendingAccountsCompleteAction
    | actions.OpenAllocationDialogAction
    | actions.CloseAllocationDialogAction
    | actions.SubmitAllocationInitAction
    | actions.SubmitAllocationCompleteAction;

export type SpendingAccountActions =
    | HandledAction
    | NetworkRequest
    | RouterAction
    | UpdatePersonMaskedSocialInsuranceNumber
    | AddNotificationAction;

let spendingAccountsPromise = null;

export const SpendingAccountActionCreators = {
    getCoveragePeriods: (): AppThunkAction<SpendingAccountActions> => (dispatch, getState: () => AppState) => {
        dispatch({ type: "GET_COVERAGE_PERIODS_INIT" });
        return dispatch(API.spendingAccounts.getCoveragePeriods())
            .then(
                (response) => {
                    if (response?.status === 200) {
                        return response.json() as Promise<MembershipCoveragePeriodSummary[]>;
                    } else {
                        dispatch({
                            type: "GET_COVERAGE_PERIODS_COMPLETE",
                            coveragePeriods: null,
                            succeeded: false,
                            error: "Failed to get spending accounts"
                        });
                        return null;
                    }
                },
                () => null
            )
            .then((coveragePeriods: MembershipCoveragePeriodSummary[]) => {
                if (coveragePeriods) {
                    dispatch({
                        type: "GET_COVERAGE_PERIODS_COMPLETE",
                        coveragePeriods,
                        succeeded: true
                    });

                    return coveragePeriods;
                }
                return null;
            });
    },
    getSpendingAccounts:
        (resetExistingSpendingAccountsPromise = false): AppThunkAction<SpendingAccountActions> =>
        (dispatch, getState: () => AppState) => {
            if (resetExistingSpendingAccountsPromise) {
                spendingAccountsPromise = null;
            }

            if (spendingAccountsPromise == null) {
                dispatch({ type: "GET_SPENDING_ACCOUNTS_INIT" });

                spendingAccountsPromise = dispatch(API.spendingAccounts.getSpendingAccounts())
                    .then(
                        (response) => {
                            if (response?.status === 200) {
                                return response.json() as Promise<MembershipSpendingAccountList[]>;
                            } else {
                                dispatch({
                                    type: "GET_SPENDING_ACCOUNTS_COMPLETE",
                                    spendingAccounts: null,
                                    succeeded: false,
                                    error: "Failed to get spending accounts"
                                });
                                return null;
                            }
                        },
                        () => null
                    )
                    .then(async (spendingAccounts: MembershipSpendingAccountList[]) => {
                        if (spendingAccounts) {
                            await dispatch({
                                type: "GET_SPENDING_ACCOUNTS_COMPLETE",
                                spendingAccounts,
                                succeeded: true
                            });

                            return spendingAccounts;
                        }
                        return null;
                    });
            }
            return spendingAccountsPromise;
        },

    openAllocationDialog: (): AppThunkAction<SpendingAccountActions> => (dispatch, getState: () => AppState) => {
        dispatch({ type: "OPEN_ALLOCATION_DIALOG" });
    },

    closeAllocationDialog: (): AppThunkAction<SpendingAccountActions> => (dispatch, getState: () => AppState) => {
        dispatch({ type: "CLOSE_ALLOCATION_DIALOG" });
    },
    submitAllocation:
        (
            hsaAllocation: number,
            wsaAllocation: number,
            tfsaAllocation: number,
            rrspAllocation: number,
            coveragePeriodId: number,
            selectedSpendingAccountId: number,
            membershipId: number,
            socialInsuranceNumber?: string
        ): AppThunkAction<SpendingAccountActions> =>
        async (dispatch, getState: () => AppState) => {
            // Re-checked here rather than by disabling the button, so the dialog and its values survive.
            if (await isSaveBlockedByMonthEndMaintenance(dispatch, getState, "Spending account allocation")) {
                return false;
            }

            dispatch({ type: "SUBMIT_ALLOCATION_INIT" });

            return dispatch(
                API.spendingAccounts.submitAllocation(
                    hsaAllocation,
                    wsaAllocation,
                    coveragePeriodId,
                    membershipId,
                    socialInsuranceNumber,
                    tfsaAllocation,
                    rrspAllocation
                )
            )
                .then((response) => {
                    if (response?.status === 200) {
                        return response.json() as Promise<number[]>;
                    } else {
                        dispatch({
                            type: "SUBMIT_ALLOCATION_COMPLETE",
                            succeeded: false,
                            error: "Failed to set allocation amount."
                        });
                        return null;
                    }
                })
                .then((spendingAccountIds: number[]) => {
                    if (spendingAccountIds) {
                        dispatch(SpendingAccountActionCreators.getCoveragePeriods());
                        dispatch(SpendingAccountActionCreators.getSpendingAccounts(true));
                        if (spendingAccountIds.indexOf(selectedSpendingAccountId) < 0) {
                            selectedSpendingAccountId = spendingAccountIds[0];
                            dispatch(push(`/benefits/spending-accounts`));
                        }
                        if (socialInsuranceNumber) {
                            dispatch({
                                type: "UPDATE_PERSON_MASKED_SOCIALINSURANCENUMBER",
                                maskedSocialInsuranceNumber: Formatter.maskSocialInsuranceNumber(socialInsuranceNumber)
                            });
                        }
                        dispatch({ type: "SUBMIT_ALLOCATION_COMPLETE", succeeded: true });
                        dispatch({
                            type: "ADD_NOTIFICATION",
                            message: "Success! Your allocation is set.",
                            style: NotificationStyle.Success,
                            autohide: true,
                            canDismiss: true
                        });
                    } else {
                        dispatch({
                            type: "SUBMIT_ALLOCATION_COMPLETE",
                            succeeded: false,
                            error: "Failed to set allocation amount."
                        });
                    }
                    return null;
                });
        }
};
